import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Card, DatePicker, Select, Space, Form, Divider, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { useStore } from '../../../stores/root-store-context';
import { CrmPeriodBucket, CrmReportDimension, CrmReportEntity, CrmReportRequest } from '../../../api/CRMApiClient';
import ReportCard from './ReportCard';
import { REPORT_PRESETS, ENTITY_OPTIONS, DIMENSION_OPTIONS, CHART_OPTIONS, ReportChartType } from './reportPresets';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const BUCKETS: CrmPeriodBucket[] = ['Day', 'Week', 'Month', 'Quarter', 'Year'];

const CrmReports: React.FC = observer(() => {
  const { t } = useTranslation();
  const { crmStore, employeesStore } = useStore();

  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs().subtract(11, 'month').startOf('month'), dayjs().endOf('month')]);
  const [userIds, setUserIds] = useState<number[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [eventTypeIds, setEventTypeIds] = useState<number[]>([]);
  const [bucket, setBucket] = useState<CrmPeriodBucket>('Month');

  const [custom, setCustom] = useState<{ entity: CrmReportEntity; groupBy: CrmReportDimension; secondaryGroupBy?: CrmReportDimension; chart: ReportChartType }>({
    entity: 'Events', groupBy: 'EventType', chart: 'pie',
  });

  useEffect(() => {
    crmStore.getExternalCompanies();
    crmStore.getCrmEventTypes();
    employeesStore.getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const baseFilters = useMemo<Omit<CrmReportRequest, 'entity' | 'groupBy'>>(() => ({
    bucket,
    dateFrom: range[0] ? range[0].startOf('day').toISOString() : undefined,
    dateTo: range[1] ? range[1].endOf('day').toISOString() : undefined,
    userIds: userIds.length ? userIds : undefined,
    countries: countries.length ? countries : undefined,
    cities: cities.length ? cities : undefined,
    eventTypeIds: eventTypeIds.length ? eventTypeIds : undefined,
    timeZoneOffsetMinutes: -new Date().getTimezoneOffset(),
  }), [bucket, range, userIds, countries, cities, eventTypeIds]);

  const users = employeesStore.users ?? [];
  // Country / city options come from the addresses of this tenant's companies (trimmed, de-duplicated case-insensitively)
  const distinctAddressValues = (pick: (a: any) => string | undefined): string[] => {
    const seen = new Map<string, string>();
    crmStore.externalCompanies.forEach(c => (c.addresses || []).forEach((a: any) => {
      const v = (pick(a) || '').trim();
      if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
    }));
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  };
  const countryOptions = distinctAddressValues(a => a.country);
  const cityOptions = distinctAddressValues(a => a.city);
  const dimLabel = (d: CrmReportDimension) => t(d === 'City' ? 'CityDistrict' : d);

  return (
    <div style={{ paddingTop: 16 }}>
      <Card bordered={false} className="crm-report-filters">
        <Space wrap size="middle">
          <RangePicker
            value={range}
            allowEmpty={[true, true]}
            format="DD/MM/YYYY"
            onChange={v => setRange([v?.[0] ?? null, v?.[1] ?? null])}
          />
          <Select<CrmPeriodBucket> value={bucket} onChange={setBucket} style={{ width: 130 }}>
            {BUCKETS.map(b => <Select.Option key={b} value={b}>{t(b)}</Select.Option>)}
          </Select>
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder={t('AllUsers')} style={{ minWidth: 220 }}
            value={userIds} onChange={setUserIds} optionFilterProp="children">
            {users.map(u => <Select.Option key={u.id} value={Number(u.id)}>{`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email}</Select.Option>)}
          </Select>
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder={t('AllCountries')} style={{ minWidth: 180 }}
            value={countries} onChange={setCountries} optionFilterProp="children">
            {countryOptions.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
          </Select>
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder={t('AllCities')} style={{ minWidth: 180 }}
            value={cities} onChange={setCities} optionFilterProp="children">
            {cityOptions.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
          </Select>
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder={t('EventTypes')} style={{ minWidth: 200 }}
            value={eventTypeIds} onChange={setEventTypeIds} optionFilterProp="children">
            {crmStore.CrmEventTypes.map(et => <Select.Option key={et.id} value={et.id}>{et.name}</Select.Option>)}
          </Select>
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {REPORT_PRESETS.map(p => (
          <Col xs={24} xl={12} key={p.key}>
            <ReportCard
              title={t(p.titleKey)}
              chart={p.chart}
              request={{ ...baseFilters, entity: p.entity, groupBy: p.groupBy, secondaryGroupBy: p.secondaryGroupBy }}
            />
          </Col>
        ))}
      </Row>

      <Divider />
      <Title level={5}>{t('CustomReport')}</Title>
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Item label={t('Entity')}>
            <Select<CrmReportEntity> value={custom.entity} onChange={v => setCustom({ ...custom, entity: v })} style={{ width: 150 }}>
              {ENTITY_OPTIONS.map(e => <Select.Option key={e} value={e}>{t(e)}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item label={t('GroupBy')}>
            <Select<CrmReportDimension> value={custom.groupBy} onChange={v => setCustom({ ...custom, groupBy: v, secondaryGroupBy: custom.secondaryGroupBy === v ? undefined : custom.secondaryGroupBy })} style={{ width: 160 }}>
              {DIMENSION_OPTIONS.map(d => <Select.Option key={d} value={d}>{dimLabel(d)}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item label={t('SecondaryGroupBy')}>
            <Select<CrmReportDimension | undefined> allowClear value={custom.secondaryGroupBy} onChange={v => setCustom({ ...custom, secondaryGroupBy: v })} style={{ width: 160 }}>
              {DIMENSION_OPTIONS.filter(d => d !== custom.groupBy).map(d => <Select.Option key={d} value={d}>{dimLabel(d)}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item label={t('Chart')}>
            <Select<ReportChartType> value={custom.chart} onChange={v => setCustom({ ...custom, chart: v })} style={{ width: 150 }}>
              {CHART_OPTIONS.map(c => <Select.Option key={c} value={c}>{t(`Chart_${c}`)}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Card>
      <ReportCard
        title={`${t(custom.entity)} / ${dimLabel(custom.groupBy)}${custom.secondaryGroupBy ? ` × ${dimLabel(custom.secondaryGroupBy)}` : ''}`}
        chart={custom.chart}
        request={{ ...baseFilters, entity: custom.entity, groupBy: custom.groupBy, secondaryGroupBy: custom.secondaryGroupBy }}
      />
    </div>
  );
});

export default CrmReports;
